import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UNITS } from "@/lib/units";

const Section = ({ title, children }) => (
  <Card className="p-4 space-y-3">
    <div className="text-sm font-semibold uppercase tracking-wider opacity-70">{title}</div>
    {children}
  </Card>
);
const Fld = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs opacity-70">{label}</Label>
    {children}
  </div>
);

const empty = {
  name: "", code: "", customer_id: "", project_type: "", description: "",
  priority: "medium", status: "draft",
  start_date: "", expected_end_date: "", actual_completion_date: "",
  project_manager_id: "",
  project_value: 0, budget: 0, gst_pct: 18,
  po_number: "", po_date: "", contract_number: "",
  location: "", site_address: "", site_contact_name: "", site_contact_phone: "",
  notes: "",
  line_items: [],
};

export default function ProjectForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/accounts").then((r) => setCustomers(r.data || []));
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data || []));
    api.get("/products").then((r) => setProducts(r.data || []));
    api.get("/projects/statuses").then((r) => { setStatuses(r.data.statuses); setPriorities(r.data.priorities); });
    if (isEdit) {
      api.get(`/projects/${id}`).then((r) => setForm({ ...empty, ...r.data }));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Project name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Coerce numerics
      ["project_value", "budget", "gst_pct"].forEach((k) => { payload[k] = Number(payload[k] || 0); });
      const r = isEdit ? await api.put(`/projects/${id}`, payload) : await api.post("/projects", payload);
      toast.success(isEdit ? "Project updated" : "Project created");
      navigate(`/projects/${r.data.id || id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <PageHeader title={isEdit ? "Edit Project" : "New Project"}
                  description="All fields marked with * are required."
                  action={
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
                      <Button type="submit" disabled={saving} data-testid="save-project-btn">
                        {saving ? "Saving…" : (isEdit ? "Update" : "Create")}
                      </Button>
                    </div>
                  } />

      <Section title="Basic Information">
        <div className="grid md:grid-cols-2 gap-3">
          <Fld label="Project Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="project-name-input" /></Fld>
          <Fld label="Project Code (auto if blank)"><Input value={form.code} onChange={(e) => set("code", e.target.value)} placeholder="PRJ-2026-0001" /></Fld>
          <Fld label="Customer"><Select value={form.customer_id || "__none__"} onValueChange={(v) => set("customer_id", v === "__none__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
            <SelectContent><SelectItem value="__none__">— No customer —</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select></Fld>
          <Fld label="Project Type"><Input value={form.project_type} onChange={(e) => set("project_type", e.target.value)} placeholder="Installation / AMC / Retrofit …" /></Fld>
          <Fld label="Priority"><Select value={form.priority} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{priorities.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
          </Select></Fld>
          <Fld label="Status"><Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select></Fld>
        </div>
        <Fld label="Description"><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></Fld>
      </Section>

      <Section title="Project Timeline">
        <div className="grid md:grid-cols-3 gap-3">
          <Fld label="Start Date"><Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} /></Fld>
          <Fld label="Expected End Date"><Input type="date" value={form.expected_end_date || ""} onChange={(e) => set("expected_end_date", e.target.value)} /></Fld>
          <Fld label="Actual Completion Date"><Input type="date" value={form.actual_completion_date || ""} onChange={(e) => set("actual_completion_date", e.target.value)} /></Fld>
        </div>
      </Section>

      <Section title="Project Team">
        <Fld label="Project Manager"><Select value={form.project_manager_id || "__none__"} onValueChange={(v) => set("project_manager_id", v === "__none__" ? "" : v)}>
          <SelectTrigger><SelectValue placeholder="Select project manager" /></SelectTrigger>
          <SelectContent><SelectItem value="__none__">— None —</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
          </SelectContent>
        </Select></Fld>
        <div className="text-xs opacity-60">Add team members from the project details page after creating the project.</div>
      </Section>

      <Section title="Commercial Information">
        <div className="grid md:grid-cols-3 gap-3">
          <Fld label="Project Value (₹)"><Input type="number" step="0.01" value={form.project_value} onChange={(e) => set("project_value", e.target.value)} /></Fld>
          <Fld label="Budget (₹)"><Input type="number" step="0.01" value={form.budget} onChange={(e) => set("budget", e.target.value)} /></Fld>
          <Fld label="GST %"><Input type="number" step="0.01" value={form.gst_pct} onChange={(e) => set("gst_pct", e.target.value)} /></Fld>
          <Fld label="PO Number"><Input value={form.po_number} onChange={(e) => set("po_number", e.target.value)} /></Fld>
          <Fld label="PO Date"><Input type="date" value={form.po_date || ""} onChange={(e) => set("po_date", e.target.value)} /></Fld>
          <Fld label="Contract Number"><Input value={form.contract_number} onChange={(e) => set("contract_number", e.target.value)} /></Fld>
        </div>
      </Section>

      <Section title="Site Information">
        <div className="grid md:grid-cols-2 gap-3">
          <Fld label="Project Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, State" /></Fld>
          <Fld label="Site Contact Name"><Input value={form.site_contact_name} onChange={(e) => set("site_contact_name", e.target.value)} /></Fld>
          <Fld label="Site Contact Number"><Input value={form.site_contact_phone} onChange={(e) => set("site_contact_phone", e.target.value)} /></Fld>
        </div>
        <Fld label="Site Address"><Textarea rows={2} value={form.site_address} onChange={(e) => set("site_address", e.target.value)} /></Fld>
      </Section>

      <Section title="Additional Information">
        <Fld label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Fld>
      </Section>

      <Section title="Line Items / Scope of Work">
        <div className="text-xs opacity-60 -mt-2">Pick products/services from your catalog. Purchase & sell prices auto-fill from the catalog and margins compute automatically.</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase opacity-70">
              <tr>
                <th className="text-left py-1 min-w-[220px]">Product / Service</th>
                <th className="text-left py-1 w-16">Qty</th>
                <th className="text-left py-1 w-24">Unit</th>
                <th className="text-right py-1 w-24">Unit Cost</th>
                <th className="text-right py-1 w-28">Total Cost</th>
                <th className="text-right py-1 w-24">Unit Sell</th>
                <th className="text-right py-1 w-28">Total Sell</th>
                <th className="text-right py-1 w-16">Tax %</th>
                <th className="text-right py-1 w-24">Margin</th>
                <th className="text-right py-1 w-16">Margin %</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(form.line_items || []).map((li, idx) => {
                const qty = Number(li.quantity) || 0;
                const uc = Number(li.unit_purchase_price) || 0;
                const us = Number(li.unit_sell_price) || 0;
                const totalCost = qty * uc;
                const totalSell = qty * us;
                const margin = totalSell - totalCost;
                const marginPct = totalSell > 0 ? (margin / totalSell) * 100 : 0;
                const updateRow = (patch) => {
                  const items = [...form.line_items]; items[idx] = { ...items[idx], ...patch }; set("line_items", items);
                };
                const chooseProduct = (val) => {
                  const p = products.find((x) => x.name === val || x.code === val);
                  if (p) {
                    updateRow({
                      description: p.name, product_id: p.id, product_code: p.code,
                      unit: p.primary_unit || "nos",
                      unit_purchase_price: p.purchase_price || 0,
                      unit_sell_price: p.selling_price || 0,
                      tax_pct: p.tax_pct ?? 18,
                    });
                  } else {
                    updateRow({ description: val, product_id: null });
                  }
                };
                return (
                  <tr key={idx} className="border-t">
                    <td className="py-1 pr-2">
                      <input list={`product-list-${idx}`} value={li.description || ""}
                             onChange={(e) => chooseProduct(e.target.value)}
                             placeholder="Pick from catalog or type…" data-testid={`line-product-${idx}`}
                             className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs" />
                      <datalist id={`product-list-${idx}`}>
                        {products.map((p) => <option key={p.id} value={p.name}>{p.code} · {p.kind === "service" ? "Service" : "Product"} · ₹{p.selling_price}</option>)}
                      </datalist>
                    </td>
                    <td className="py-1 pr-2"><Input className="h-8 text-xs" type="number" step="0.01" value={li.quantity ?? 1} onChange={(e) => updateRow({ quantity: e.target.value })} /></td>
                    <td className="py-1 pr-2">
                      <input list="proj-unit-list" value={li.unit || "nos"} onChange={(e) => updateRow({ unit: e.target.value })}
                             className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs" />
                    </td>
                    <td className="py-1 pr-2"><Input className="h-8 text-xs text-right" type="number" step="0.01" value={li.unit_purchase_price ?? 0} onChange={(e) => updateRow({ unit_purchase_price: e.target.value })} /></td>
                    <td className="py-1 pr-2 text-right font-mono">₹{totalCost.toFixed(2)}</td>
                    <td className="py-1 pr-2"><Input className="h-8 text-xs text-right" type="number" step="0.01" value={li.unit_sell_price ?? 0} onChange={(e) => updateRow({ unit_sell_price: e.target.value })} /></td>
                    <td className="py-1 pr-2 text-right font-mono">₹{totalSell.toFixed(2)}</td>
                    <td className="py-1 pr-2"><Input className="h-8 text-xs text-right" type="number" step="0.01" value={li.tax_pct ?? 18} onChange={(e) => updateRow({ tax_pct: e.target.value })} /></td>
                    <td className={`py-1 pr-2 text-right font-mono ${margin >= 0 ? "text-green-700" : "text-red-600"}`}>₹{margin.toFixed(2)}</td>
                    <td className={`py-1 pr-2 text-right font-mono ${marginPct >= 0 ? "text-green-700" : "text-red-600"}`}>{marginPct.toFixed(1)}%</td>
                    <td className="py-1"><Button type="button" variant="ghost" size="icon" onClick={() => { const items = form.line_items.filter((_, i) => i !== idx); set("line_items", items); }}>×</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="proj-unit-list">{UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}</datalist>
        </div>
        <Button type="button" variant="outline" size="sm" data-testid="add-line-item-btn"
                onClick={() => set("line_items", [...(form.line_items || []), { description: "", quantity: 1, unit: "nos", unit_purchase_price: 0, unit_sell_price: 0, tax_pct: 18 }])}>
          + Add Line Item
        </Button>
        {(form.line_items || []).length > 0 && (() => {
          const totals = (form.line_items || []).reduce((s, li) => {
            const q = Number(li.quantity) || 0;
            const tc = q * (Number(li.unit_purchase_price) || 0);
            const ts = q * (Number(li.unit_sell_price) || 0);
            s.cost += tc; s.sell += ts;
            return s;
          }, { cost: 0, sell: 0 });
          totals.margin = totals.sell - totals.cost;
          totals.marginPct = totals.sell > 0 ? (totals.margin / totals.sell) * 100 : 0;
          return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t text-sm">
              <div><div className="text-xs opacity-60">Total Cost</div><div className="font-mono">₹{totals.cost.toFixed(2)}</div></div>
              <div><div className="text-xs opacity-60">Total Sell</div><div className="font-mono">₹{totals.sell.toFixed(2)}</div></div>
              <div><div className="text-xs opacity-60">Total Margin</div><div className={`font-mono font-semibold ${totals.margin >= 0 ? "text-green-700" : "text-red-600"}`}>₹{totals.margin.toFixed(2)}</div></div>
              <div><div className="text-xs opacity-60">Margin %</div><div className={`font-mono font-semibold ${totals.marginPct >= 0 ? "text-green-700" : "text-red-600"}`}>{totals.marginPct.toFixed(2)}%</div></div>
            </div>
          );
        })()}
      </Section>
    </form>
  );
}
